import { gql, useQuery } from '@apollo/client';
import { usePoolData } from '@/hooks/usePoolData';
import { useIncentiveStakingData } from '@/hooks/useIncentiveStakingData';
import { calculateIncentiveAPR } from '@/utils/common';

export const YEAR = 31536000;

const GET_INCENTIVE = (id: string) => gql`
  query {
    incentiveInfo(id: "${id}") {
      id
      rewardToken
      pool
      startTime
      endTime
      refundee
      reward
      refundAmount
    }
  }
`;

/**
 * Hook to calculate APR for an incentive
 *
 * IMPORTANT: APR is calculated based on STAKED liquidity, not pool liquidity.
 * This is because Uniswap V3 Staker distributes rewards only among staked positions.
 *
 * Formula:
 * APR = (Reward Amount × Reward Token Price / Total Staked Value) × (Seconds per Year / Incentive Duration) × 100%
 *
 * @see docs/spec/06-apr-calculation.md for detailed specification
 */
export function useIncentiveAPR(id: string): string | number {
  const { data: incentiveData, loading: loadingIncentive } = useQuery(
    GET_INCENTIVE(id),
    { skip: !id }
  );
  const { totalStakedValue, isLoading: loadingStaking } = useIncentiveStakingData(id);
  const poolData = usePoolData();

  // Return 0 while loading or if no data
  if (loadingIncentive || loadingStaking || poolData.isLoading) return 0;
  if (!incentiveData?.incentiveInfo) return 0;

  const { reward, startTime, endTime } = incentiveData.incentiveInfo;

  // If no staked value, APR cannot be calculated (would be infinite)
  if (totalStakedValue <= 0) return 0;

  // Validate startTime and endTime to prevent division by zero
  // Note: GraphQL returns these as strings, so convert to BigInt before comparison
  if (!startTime || !endTime) return 0;
  const startTimeBigInt = BigInt(startTime);
  const endTimeBigInt = BigInt(endTime);
  if (startTimeBigInt >= endTimeBigInt) return 0;

  const apr = calculateIncentiveAPR(
    BigInt(reward || 0),
    poolData.jocxPrice,
    totalStakedValue,
    startTimeBigInt,
    endTimeBigInt
  );

  return apr.toFixed(2);
}
